import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  TextInput,
  SelectDropdown,
  RequiredFieldIndicator,
} from '@gsa/afp-component-library';

import { INPUT_DROPDOWN_DEFAULT_OPTION, COLOR_TYPE } from './colors-table-defs';
import { useContractLine } from '../provider/contract-line-provider';

import { ReadOnlyWrapper } from '../helpers/ec-components';

const AddColor = ({ selectedColor, showEdit, setShowEdit }) => {
  const {
    contractLine,
    isAwardedInFleet,
    colorsTabData,
    setColorsTabData,
    gsaColors,
    setAlert,
    canUpdateContract,
    canVendorUpdateContract,
  } = useContractLine();
  const colorNameRef = useRef(null);

  const canUpdateColor = canUpdateContract || canVendorUpdateContract;

  const colors = colorsTabData || [];

  const requiredText = 'This is a required field';
  const schema = yup.object().shape({
    vendorColorCode: yup
      .string()
      .required(requiredText)
      .matches(
        /^[a-zA-Z0-9 _\-/]+$/,
        'Special characters not allowed except "-", "_", "/" or space',
      )
      .matches(
        /[a-zA-Z0-9]+/,
        'Must contain at least one alphanumeric character',
      )
      .test('color-exists', 'Color code already exists', function (code) {
        if (showEdit) return true; // skip validation if editing an existing color
        // eslint-disable-next-line react/no-this-in-sfc
        const { existingColors } = this.parent;
        return !existingColors.some(
          (c) => c.vendorColorCode.toUpperCase() === code.toUpperCase(),
        );
      }),
    vendorColorName: yup
      .string()
      .required(requiredText)
      .matches(
        /[a-zA-Z0-9]+/,
        'Must contain at least one alphanumeric character',
      ),
    gsaColorCodeId: yup.string().when([], {
      is: () => isAwardedInFleet,
      then: yup.string().required(requiredText),
      otherwise: yup.string(),
    }),
    colorType: yup.string().required(requiredText),
    price: yup
      .number()
      .transform((value, originalValue) => {
        // Convert empty string to undefined so Yup skips number validation
        return originalValue === '' ? undefined : value;
      })
      .when('colorType', {
        is: (colorType) => {
          return colorType === COLOR_TYPE.optional;
        },
        then: yup
          .number()
          .transform((value, originalValue) => {
            return originalValue === '' ? undefined : value;
          })
          .integer('Must be a whole number')
          .typeError(requiredText)
          .min(1, 'Must be $1 or more')
          .max(9999, 'Must be less than $10,000')
          .required(requiredText),
        // If colorType is not optional, we don't require price and allow null values
        otherwise: yup
          .number()
          .transform((value, originalValue) => {
            return originalValue === '' ? undefined : value;
          })
          .notRequired()
          .nullable(),
      }),
  });

  const {
    formState: { errors },
    watch,
    control,
    handleSubmit,
    reset,
    setValue,
    clearErrors,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      vendorColorName: '',
      vendorColorCode: '',
      gsaColorCodeId: '',
      colorType: !isAwardedInFleet ? COLOR_TYPE.standard : '',
      price: '',
      existingColors: colors,
    },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });
  const colorType = watch('colorType');

  useEffect(() => {
    setValue('existingColors', colors);
  }, [colors]);

  useEffect(() => {
    if (showEdit && selectedColor) {
      clearErrors();
      setValue('vendorColorName', selectedColor.vendorColorName);
      setValue('vendorColorCode', selectedColor.vendorColorCode);
      setValue('gsaColorCodeId', selectedColor.gsaColorCodeId);
      setValue('colorType', selectedColor.standardPremiumCode);
      setValue('price', selectedColor.price);
      colorNameRef.current.focus();
    } else {
      reset();
    }
  }, [showEdit, selectedColor]);

  const gsaColorOptions = [INPUT_DROPDOWN_DEFAULT_OPTION];
  gsaColors?.forEach((color) =>
    gsaColorOptions.push({ label: color.title, value: color.id }),
  );

  const colorTypeOptions = useMemo(() => {
    return canUpdateContract ||
      (showEdit && selectedColor?.standardPremiumCode === COLOR_TYPE.optional)
      ? [
          INPUT_DROPDOWN_DEFAULT_OPTION,
          { label: 'Standard', value: COLOR_TYPE.standard },
          { label: 'Optional', value: COLOR_TYPE.optional },
        ]
      : [
          INPUT_DROPDOWN_DEFAULT_OPTION,
          { label: 'Standard', value: COLOR_TYPE.standard },
        ];
  }, [canUpdateContract, showEdit, selectedColor?.standardPremiumCode]);

  const onSubmit = useCallback(
    (data) => {
      const newColor = {
        contractLineId: +contractLine.id,
        vendorColorName: data.vendorColorName,
        vendorColorCode: data.vendorColorCode,
        gsaColorCodeId: +data.gsaColorCodeId,
        standardPremiumCode: data.colorType,
        price: data.price,
      };
      setColorsTabData((prev) => {
        const colors = prev || [];
        // edit - replace existing color
        if (showEdit) {
          const colorIndex = colors.findIndex(
            (c) => c.vendorColorCode === newColor.vendorColorCode,
          );
          return colors.toSpliced(colorIndex, 1, newColor);
        }

        // add new color
        return [newColor, ...colors];
      });
      setAlert(null);
      reset();
      setShowEdit(false);
    },
    [contractLine, showEdit],
  );

  const genLabel = (label, required) => (
    <span className="text-bold">
      {label} {required && <RequiredFieldIndicator />}
    </span>
  );

  return (
    <div className="bg-primary-lightest padding-bottom-2">
      <form
        id="commenting-period-schedule-form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Controller
          name="existingColors"
          control={control}
          render={() => null}
        />
        <div className="display-flex flex-row flex-justify flex-align-start margin-x-2">
          <div
            className={`grid-col-2 ${
              errors.vendorColorCode ? 'padding-left-2' : ''
            }`}
          >
            <Controller
              name="vendorColorCode"
              control={control}
              render={({ field: { name, value, onChange, onBlur } }) => (
                <ReadOnlyWrapper wrapperType="grid">
                  <TextInput
                    name={name}
                    data-testid="add-color-vendor-color-code"
                    type="text"
                    label={genLabel('Vendor color code', true)}
                    maxLength={10}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    errorMessage={errors.vendorColorCode?.message}
                    disabled={!canUpdateColor || showEdit}
                  />
                </ReadOnlyWrapper>
              )}
            />
          </div>
          <div
            className={`grid-col-2 ${
              errors.vendorColorName ? 'padding-left-2' : ''
            }`}
          >
            <Controller
              name="vendorColorName"
              control={control}
              defaultValue=""
              render={({ field: { name, value, onChange, onBlur } }) => (
                <ReadOnlyWrapper wrapperType="grid">
                  <TextInput
                    name={name}
                    data-testid="add-color-vendor-color-name"
                    type="text"
                    label={genLabel('Vendor color name', true)}
                    maxLength={30}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    errorMessage={errors.vendorColorName?.message}
                    disabled={!canUpdateColor}
                    inputRef={colorNameRef}
                  />
                </ReadOnlyWrapper>
              )}
            />
          </div>
          <div
            className={`grid-col-2 ${
              errors.gsaColorCodeId ? 'padding-left-2' : ''
            }`}
          >
            <Controller
              name="gsaColorCodeId"
              control={control}
              render={({ field: { name, value, onChange, onBlur } }) => (
                <ReadOnlyWrapper wrapperType="grid">
                  <SelectDropdown
                    name={name}
                    id="add-color-gsa-color"
                    data-testid="add-color-gsa-color"
                    label={genLabel('GSA color', isAwardedInFleet)}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    errorMessage={errors.gsaColorCodeId?.message}
                    options={gsaColorOptions}
                    disabled={!canUpdateColor}
                  />
                </ReadOnlyWrapper>
              )}
            />
          </div>
          <div
            className={`grid-col-2 ${errors.colorType ? 'padding-left-2' : ''}`}
          >
            <Controller
              name="colorType"
              control={control}
              render={({ field: { name, value, onChange, onBlur } }) => (
                <ReadOnlyWrapper wrapperType="grid">
                  <SelectDropdown
                    name={name}
                    id="add-color-is-optional"
                    data-testid="add-color-standard-optional"
                    label={genLabel('Standard or optional', true)}
                    value={value}
                    onChange={(evt) => {
                      onChange(evt);
                      if (evt.target.value !== COLOR_TYPE.optional) {
                        setValue('price', ''); // reset price if not optional
                      } else if (showEdit && !canUpdateContract) {
                        // Restore price if VS editing an optional color because price is read-only
                        setValue('price', selectedColor.price);
                      }
                    }}
                    onBlur={onBlur}
                    errorMessage={errors.colorType?.message}
                    options={colorTypeOptions}
                    disabled={
                      !isAwardedInFleet ||
                      !canUpdateColor ||
                      (showEdit &&
                        !canUpdateContract &&
                        selectedColor?.standardPremiumCode ===
                          COLOR_TYPE.standard)
                    }
                  />
                </ReadOnlyWrapper>
              )}
            />
          </div>
          <div className={`grid-col-2 ${errors.price ? 'padding-left-2' : ''}`}>
            {isAwardedInFleet && colorType === 'Premium' && (
              <Controller
                name="price"
                control={control}
                render={({ field: { name, value, onChange, onBlur } }) => (
                  <ReadOnlyWrapper wrapperType="grid">
                    <TextInput
                      name={name}
                      data-testid="add-color-price"
                      type="number"
                      prefix="$"
                      label={genLabel('Price', true)}
                      maxLength={10}
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      errorMessage={errors.price?.message}
                      disabled={!canUpdateContract}
                    />
                  </ReadOnlyWrapper>
                )}
              />
            )}
          </div>
          <div className="text-right flex-align-self-end">
            {!showEdit && (
              <Button
                type="submit"
                variant="outline"
                label="Add"
                leftIcon={{ name: 'add' }}
                className="margin-left-2 margin-right-0"
                disabled={!canUpdateColor}
              />
            )}
            {showEdit && (
              <Button
                type="submit"
                variant="outline"
                label="Save"
                className="margin-left-2 margin-right-1"
              />
            )}
            {showEdit && (
              <Button
                type="button"
                variant="unstyled"
                label="Cancel"
                className="margin-right-0"
                onClick={() => {
                  setShowEdit(false);
                  setAlert(null);
                }}
              />
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

AddColor.propTypes = {
  showEdit: PropTypes.bool.isRequired,
  setShowEdit: PropTypes.func.isRequired,
  selectedColor: PropTypes.shape({
    id: PropTypes.number,
    vendorColorCode: PropTypes.string,
    vendorColorName: PropTypes.string,
    gsaColorCodeId: PropTypes.number,
    standardPremiumCode: PropTypes.string,
    price: PropTypes.number,
  }),
};

export default AddColor;
